import { throttle } from '@ember/runloop';

import { modifier } from 'ember-modifier';

/*
	Adds an attribute to an element that has overflow 'horizontal' | 'vertical' | 'both' | ''
	This attribute can then be used to apply styles to the element. For example:
		- white-space: nowrap breaks flexbox layout because flex items never shrink past content size, so usign this attribute, we can fake text-overflow: ellipsis
		- scrollbar needs to be hidden to improve UI, add another indicator that element has scroll (arrows, shadows)
*/

const overflowAttribute = modifier(
	(
		element: HTMLDivElement,

		[]: never[],
		hashParams: {
			selector?: string;
			timeout?: number;
			events?: string[];
			errorMarginX?: number; // Ignore small overflow on purpose
			errorMarginY?: number;
		} = {},
	) => {
		const selector = hashParams.selector ?? '',
			timeout = hashParams.timeout ?? 0,
			errorMarginX = hashParams.errorMarginX ?? 0,
			errorMarginY = hashParams.errorMarginY ?? 0;

		const isOverflownX = ({ clientWidth, scrollWidth }: Element) => {
			return scrollWidth > clientWidth + errorMarginX;
		};

		const isOverflownY = ({ clientHeight, scrollHeight }: Element) => {
			return scrollHeight > clientHeight + errorMarginY;
		};

		const updateAttribute = () => {
			if (target) {
				const x = isOverflownX(target),
					y = isOverflownY(target),
					attrVal = x && y ? 'both' : x ? 'horizontal' : y ? 'vertical' : '';

				target.setAttribute('dbx-overflow', attrVal);
			}
		};

		const onResizeThrottled = () => {
			updateAttribute();
		};

		const onResizeOrExternal = () => {
			throttle(this, onResizeThrottled, 500, false);
		};

		let resizeObserver: ResizeObserver | null = null;
		let target: Element | null = null;
		const handlers: [string, () => void][] = [];

		setTimeout(() => {
			// hack, to delay binding on elements created by jquery plugins like select2
			target = selector ? element.querySelector(selector) : element;

			if (target) {
				updateAttribute();
				resizeObserver = new ResizeObserver(onResizeOrExternal);
				resizeObserver.observe(target);

				const events = hashParams.events || [];
				events.forEach((eventName) => {
					const handler = () => {
						onResizeOrExternal();
					};
					handlers.push([eventName, handler]);
					element.addEventListener(eventName, handler);
				});
			}
		}, timeout);

		return () => {
			if (target) {
				if (resizeObserver) {
					resizeObserver.unobserve(target);
				}

				handlers.forEach(([eventName, handler]) => {
					element.removeEventListener(eventName, handler);
				});
			}
		};
	},
);

export default overflowAttribute;
